import React, { useEffect, useMemo, useState } from "react";
import {
  ArrowRight,
  BadgeCheck,
  Check,
  CreditCard,
  Loader2,
  ShieldCheck,
  Sparkles
} from "lucide-react";
import { formatMoneyFromMinorUnits } from "./currencyUtils.js";
import {
  getProfileSubscriptionPlan,
  isProfileSubscriptionActive
} from "./profileSubscriptionPlans.js";
import {
  useLocalizedProfileSubscriptionOffer
} from "./visitorPricingCountry.js";
import { isNativeApp } from "./nativeAppBridge.js";
import { centreMarketCapability } from "./centreMarketCapabilities.js";

const upgradeIntentStorageKey = "badminta:profile-upgrade-intent:v1";

const storeUpgradeIntent = (role, tier, billingInterval) => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(upgradeIntentStorageKey, JSON.stringify({
      role,
      tier,
      billingInterval,
      capturedAt: new Date().toISOString()
    }));
  } catch {
    // Signup remains available when browser storage is disabled.
  }
};

const planPriceLabel = (offer, billingInterval) => {
  const cents = billingInterval === "annual" ? offer.annualCents : offer.monthlyCents;
  return formatMoneyFromMinorUnits(cents, offer.currency);
};

const annualMonthlyEquivalent = (offer) => (
  formatMoneyFromMinorUnits(Math.round(offer.annualCents / 12), offer.currency)
);

const billingPeriodLabel = (billingInterval) => billingInterval === "annual" ? "year" : "month";

export function ProfilePlanPricing({
  role,
  onPrimaryAction,
  authenticated = false,
  className = ""
}) {
  const plan = getProfileSubscriptionPlan(role);
  const [billingInterval, setBillingInterval] = useState("annual");
  const { offer, pricingCountry } = useLocalizedProfileSubscriptionOffer(plan);
  const centreCapability = role === "centre" ? centreMarketCapability(offer.countryCode) : null;
  const choosePlan = (tier) => {
    storeUpgradeIntent(role, tier, billingInterval);
    onPrimaryAction?.({ role, tier, billingInterval });
  };

  return (
    <section className={`profile-plan-pricing ${className}`.trim()} aria-labelledby={`${role}-pricing-title`}>
      <div className="profile-plan-pricing-head">
        <div>
          <p><Sparkles size={16} /> Simple role pricing</p>
          <h2 id={`${role}-pricing-title`}>Start free. Upgrade when the profile is creating value.</h2>
          <span>{plan.headline} Every plan keeps its own billing, so one account can upgrade only the roles it uses.</span>
        </div>
        <div className="profile-plan-billing-toggle" role="group" aria-label="Billing frequency">
          <button type="button" className={billingInterval === "monthly" ? "active" : ""} onClick={() => setBillingInterval("monthly")}>Monthly</button>
          <button type="button" className={billingInterval === "annual" ? "active" : ""} onClick={() => setBillingInterval("annual")}>
            Annual
            <span>Save {formatMoneyFromMinorUnits(offer.annualSavingCents, offer.currency)}</span>
          </button>
        </div>
      </div>

      <div className="profile-plan-grid">
        <article className="profile-plan-card free">
          <div className="profile-plan-card-title">
            <span>Free</span>
            <h3>{plan.freeName}</h3>
            <p>{plan.freeSummary}</p>
          </div>
          <div className="profile-plan-price">
            <strong>{formatMoneyFromMinorUnits(0, offer.currency)}</strong>
            <span>No card required</span>
          </div>
          <ul>
            {plan.freeFeatures.map((feature) => <li key={feature}><Check size={16} /> {feature}</li>)}
          </ul>
          <button type="button" className="secondary" onClick={() => choosePlan("free")}>
            {authenticated ? `Use ${plan.freeName}` : "Create free profile"}
            <ArrowRight size={16} />
          </button>
        </article>

        <article className="profile-plan-card pro">
          <div className="profile-plan-recommended"><BadgeCheck size={15} /> Best for active {plan.roleLabel.toLowerCase()} profiles</div>
          <div className="profile-plan-card-title">
            <span>Pro</span>
            <h3>{plan.proName}</h3>
            <p>{plan.proSummary}</p>
          </div>
          <div className="profile-plan-price">
            <strong>{planPriceLabel(offer, billingInterval)}</strong>
            <span>per {billingPeriodLabel(billingInterval)}{billingInterval === "annual" ? ` · ${annualMonthlyEquivalent(offer)}/mo equivalent` : ""}</span>
          </div>
          <ul>
            {plan.proFeatures.map((feature) => <li key={feature}><Check size={16} /> {feature}</li>)}
          </ul>
          {isNativeApp ? (
            <p className="profile-plan-native-note">
              Existing Pro plans work in the app. New digital subscriptions are managed through Badminta on the web.
            </p>
          ) : (
            <button type="button" onClick={() => choosePlan("pro")}>
              {authenticated ? `Upgrade to ${plan.proName}` : "Create profile, then upgrade"}
              <ArrowRight size={16} />
            </button>
          )}
        </article>
      </div>

      <p className="profile-plan-fine-print" aria-live="polite">
        {pricingCountry.status === "resolving" ? "Checking your local package price. " : ""}
        Local package price for {offer.countryName} in {offer.currency.toUpperCase()}. Your verified signup country confirms the final amount at checkout. Taxes may apply; cancel future renewals from secure billing.
      </p>
      {centreCapability && (
        <div className={`centre-market-capability ${centreCapability.mode}`}>
          <ShieldCheck size={18} />
          <div>
            <strong>{centreCapability.label} in {offer.countryName}</strong>
            <span>{centreCapability.description}</span>
          </div>
        </div>
      )}
    </section>
  );
}

const subscriptionPeriodEnd = (subscription = {}) => {
  const value = Number(subscription?.currentPeriodEndMs || 0);
  if (!value) return "";
  try {
    return new Intl.DateTimeFormat(undefined, { day: "numeric", month: "short", year: "numeric" }).format(new Date(value));
  } catch {
    return "";
  }
};

const dashboardPathByRole = {
  player: "/players/dashboard",
  organiser: "/organiser",
  coach: "/coaches/dashboard",
  centre: "/centre-tools/dashboard"
};

export function ProfileSubscriptionPanel({
  role,
  callFunction,
  signedIn = true,
  compact = false,
  initialSubscription = null
}) {
  const plan = getProfileSubscriptionPlan(role);
  const [billingInterval, setBillingInterval] = useState("annual");
  const [state, setState] = useState({
    status: initialSubscription ? "ready" : signedIn ? "loading" : "signed-out",
    message: "",
    subscription: initialSubscription,
    checkoutUrl: "",
    offer: null
  });
  const active = isProfileSubscriptionActive(state.subscription || {});
  const periodEnd = subscriptionPeriodEnd(state.subscription);
  const busy = ["loading", "checkout", "portal"].includes(state.status);
  const visibleFeatures = useMemo(() => plan.proFeatures.slice(0, 4), [plan]);
  const { offer: visitorOffer } = useLocalizedProfileSubscriptionOffer(plan);
  const centreCapability = role === "centre" ? centreMarketCapability((state.offer || visitorOffer).countryCode) : null;

  const loadStatus = async (checkoutSessionId = "") => {
    if (!signedIn || !callFunction) {
      setState((current) => ({
        status: current.subscription || initialSubscription ? "ready" : "signed-out",
        message: "",
        subscription: current.subscription || initialSubscription,
        checkoutUrl: "",
        offer: current.offer
      }));
      return;
    }
    setState((current) => ({ ...current, status: "loading", message: checkoutSessionId ? "Confirming your upgrade..." : "" }));
    try {
      const result = await callFunction("getProfileSubscriptionStatus", {
        role,
        ...(checkoutSessionId ? { checkoutSessionId } : {})
      });
      setState({
        status: "ready",
        message: checkoutSessionId && result.subscription?.active ? `${plan.proName} is active.` : "",
        subscription: result.subscription || null,
        checkoutUrl: result.subscription?.checkoutUrl || "",
        offer: result.offer || null
      });
    } catch (error) {
      setState((current) => ({
        status: "error",
        message: error?.message || "Badminta could not load this subscription.",
        subscription: current.subscription || initialSubscription,
        checkoutUrl: "",
        offer: null
      }));
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const returnedRole = params.get("role");
    const checkoutSessionId = params.get("session_id") || "";
    const returnedFromCheckout = params.get("profile_subscription") === "success" && (!returnedRole || returnedRole === role);
    loadStatus(returnedFromCheckout ? checkoutSessionId : "");
  }, [role, signedIn]);

  const offer = state.offer || visitorOffer;

  const startCheckout = async () => {
    if (!signedIn || !callFunction) return;
    if (state.checkoutUrl && state.subscription?.status === "checkout_created") {
      window.location.assign(state.checkoutUrl);
      return;
    }
    setState((current) => ({ ...current, status: "checkout", message: "Opening secure Stripe Checkout..." }));
    try {
      const dashboardPath = dashboardPathByRole[role] || window.location.pathname;
      const result = await callFunction("createProfileSubscriptionCheckoutSession", {
        role,
        billingInterval,
        successUrl: `${window.location.origin}${dashboardPath}?profile_subscription=success&role=${encodeURIComponent(role)}&session_id={CHECKOUT_SESSION_ID}`,
        cancelUrl: `${window.location.origin}${dashboardPath}?profile_subscription=cancelled&role=${encodeURIComponent(role)}`,
        idempotencyKey: globalThis.crypto?.randomUUID?.() || `${role}-${billingInterval}-${Date.now()}`
      });
      if (result.alreadySubscribed) {
        setState((current) => ({
          ...current,
          status: "ready",
                  message: `${plan.proName} is already active.`,
                  subscription: result.subscription || current.subscription,
                  offer: result.offer || current.offer
        }));
        return;
      }
      if (!result.checkoutUrl) throw new Error("Stripe Checkout did not return a secure payment link.");
      window.location.assign(result.checkoutUrl);
    } catch (error) {
      setState((current) => ({
        ...current,
        status: "error",
        message: error?.message || "Badminta could not open subscription checkout."
      }));
    }
  };

  const openPortal = async () => {
    if (!signedIn || !callFunction) return;
    setState((current) => ({ ...current, status: "portal", message: "Opening secure billing..." }));
    try {
      const result = await callFunction("createProfileSubscriptionPortalSession", {
        role,
        returnUrl: window.location.href
      });
      if (!result.portalUrl) throw new Error("Stripe Billing did not return a secure portal link.");
      window.location.assign(result.portalUrl);
    } catch (error) {
      setState((current) => ({
        ...current,
        status: "error",
        message: error?.message || "Badminta could not open subscription billing."
      }));
    }
  };

  return (
    <section id={`${role}-subscription`} className={`profile-subscription-panel ${active ? "active" : "free"} ${compact ? "compact" : ""}`} aria-label={`${plan.roleLabel} subscription`}>
      <div className="profile-subscription-summary">
        <span className="profile-subscription-icon">{active ? <BadgeCheck size={21} /> : <Sparkles size={21} />}</span>
        <div>
          <p>{active ? `${plan.proName} active` : `${plan.freeName} · Optional upgrade`}</p>
          <h2>{active ? "Your Pro profile tools are unlocked." : plan.headline}</h2>
          <span>
            {active
              ? state.subscription?.cancelAtPeriodEnd
                ? `Pro remains active until ${periodEnd || "the end of this billing period"}.`
                : `Billing is current${periodEnd ? ` · next period ${periodEnd}` : ""}.`
              : plan.proSummary}
          </span>
        </div>
      </div>

      <div className="profile-subscription-benefits">
        {visibleFeatures.map((feature) => <span key={feature}><Check size={14} /> {feature}</span>)}
      </div>

      <div className="profile-subscription-action">
        {active ? (
          <>
            <strong>{plan.proName}</strong>
            <span>{String(state.subscription?.billingInterval || "monthly") === "annual" ? "Annual billing" : "Monthly billing"}</span>
            <button type="button" onClick={openPortal} disabled={busy}>
              {state.status === "portal" ? <Loader2 className="spin" size={16} /> : <CreditCard size={16} />}
              Manage billing
            </button>
          </>
        ) : isNativeApp ? (
          <p className="profile-plan-native-note">
            Your free profile is fully available here. Existing Pro access is recognised automatically.
          </p>
        ) : (
          <>
            <div className="profile-subscription-toggle" role="group" aria-label={`${plan.proName} billing frequency`}>
              <button type="button" className={billingInterval === "monthly" ? "active" : ""} onClick={() => setBillingInterval("monthly")}>
                {formatMoneyFromMinorUnits(offer.monthlyCents, offer.currency)}/mo
              </button>
              <button type="button" className={billingInterval === "annual" ? "active" : ""} onClick={() => setBillingInterval("annual")}>
                {formatMoneyFromMinorUnits(offer.annualCents, offer.currency)}/yr
              </button>
            </div>
            <button type="button" className="primary" onClick={startCheckout} disabled={busy || !signedIn}>
              {busy ? <Loader2 className="spin" size={16} /> : <Sparkles size={16} />}
              {state.checkoutUrl && state.subscription?.status === "checkout_created" ? "Finish upgrade" : `Upgrade to ${plan.proName}`}
            </button>
            <small><ShieldCheck size={13} /> {offer.countryName} price in {offer.currency.toUpperCase()} · secure Stripe billing</small>
          </>
        )}
      </div>

      {state.message && <div className={`profile-subscription-message ${state.status}`}>{state.message}</div>}
      {centreCapability && (
        <div className={`centre-market-capability ${centreCapability.mode}`}>
          <ShieldCheck size={17} />
          <div>
            <strong>{centreCapability.label}</strong>
            <span>{centreCapability.description}</span>
          </div>
        </div>
      )}
    </section>
  );
}
